import React, { PureComponent } from 'react';
import { notification } from 'antd';
import * as less from 'less';
import { Provider } from 'react-redux';

import { ServiceFactory } from './service/ServiceFactory';
import TimeUtils from './common/util/TimeUtils';
import { ConfigData, DatasetType } from './model/CORE';
import AppContext from './common/context/AppContext';
import NmocDashboard from './dashboard/NmocDashboard'; //IMPORTANT: import can't have {} otherwise will ask for props derived from redux state
import { FmpDashboard } from './dashboard/FmpDashboard';
import Utils from './common/util/Utils';
import AuthorisationUtils from './common/context/AuthorisationUtils';
import { store } from './redux/store';
import { UnityApplication } from './unity/UnityApplication';
import { NMUI4DContext, NMUI4DContextInitializer } from './common/context/NMUI4DContext';
import { ConsoleLoggingOverrider } from './tracing/ConsoleLoggingOverrider';
import { WMTSInitializer } from './tiling/WMTSInitializer';
import { MapTiling } from './tiling/MapTiling';

// IMPORTANT(bjarne): This needs to be included down here to prevent errors.
import FmpMapDashboard from 'app/dashboard/FmpMapDashboard';

interface AppState {
    configData: ConfigData;
}

export default class App extends PureComponent<any, AppState> {
    private opts: AddEventListenerOptions & EventListenerOptions = { passive: false };

    state = {
        configData: undefined,
    };

    private handleWheel = e => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
        }
    };

    private handleKeyDown = e => {
        if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=')) {
            e.preventDefault();
        }
    };

    componentDidMount(): void {
        this.initAppContext();

        // Disable zoom-in/zoom-out
        window.addEventListener('wheel', this.handleWheel, this.opts);
        window.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener('wheel', this.handleWheel, this.opts);
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    initAppContext = () => {
        ServiceFactory.technicalService()
            .getConfigData()
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                } else {
                    this.onConfigData(reply.data);
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    };

    private onConfigData = (data: ConfigData) => {
        AppContext.configData = data;

        // Override locally stored dataset, if retrieved data doesn't correspond to current dataset
        const datasetType = AppContext.configData?.properties['dataset'];
        if (datasetType && datasetType !== AppContext.getDataset()) {
            AppContext.setDataset({ type: datasetType as DatasetType });
        }

        // Init dashboard color tones
        less.modifyVars({
            '@base-color': AppContext.DATASET_COLOR[AppContext.getDataset().type],
        }).catch(err => console.error('Error updating less ! ', err));

        if (data.simulationTimeSynch) {
            TimeUtils.setSimulationTimeSync(data.simulationTimeSynch);
        }

        this.setState({
            configData: data,
        });
    };

    render() {
        if (Utils.isNotExists(this.state.configData)) return null;

        let dashboard: JSX.Element = undefined;

        // TODO(bjarne): This is a quick and dirty way of allowing the Vuplex window to communicate with Unity
        // without opening the "map" on the React side. Once we refactor all of this code, do it properly. Right
        // now there would probably be duplicate communication if you end up (accidentally) opening the map.
        const mode = new URLSearchParams(window.location.search).get('mode') === 'mapless' ? 'mapless' : 'default';

        if (AuthorisationUtils.isFmpUser()) {
            dashboard = <FmpDashboard configData={this.state.configData} />;
        } else if (AuthorisationUtils.isFmpMapUser()) {
            dashboard = (
                <Provider store={store}>
                    <FmpMapDashboard />
                </Provider>
            );
        } else {
            dashboard = (
                <Provider store={store}>
                    <NmocDashboard configData={this.state.configData} />
                </Provider>
            );
        }

        return (
            <NMUI4DContext.Provider
                value={{
                    logger: undefined,
                    tracer: undefined,
                    unityUtils: undefined,
                    unityRequests: undefined,
                    flightEnricher: undefined,
                    isMapless: mode === 'mapless',
                    mapTileFetcher: undefined,
                    eventBus: AppContext.eventBus,
                    unityNotificationListener: undefined,
                }}>
                <NMUI4DContextInitializer></NMUI4DContextInitializer>
                <ConsoleLoggingOverrider></ConsoleLoggingOverrider>
                <WMTSInitializer></WMTSInitializer>
                <MapTiling></MapTiling>
                {mode === 'mapless' && <UnityApplication></UnityApplication>}
                {dashboard}
            </NMUI4DContext.Provider>
        );
    }
}
